
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../../components/docs/Actions';
import styles from 'src/components/docs/Image/index.module.scss';
export const metadata = {
  toc: true,
  description: 'Configure end users&#39; options for verifying their account with Reroute to Credentials and Auth Type Select'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Configuring Auth entry points' subtitle='Configure end users&#39; options for verifying their account with Reroute to Credentials and Auth Type Select' mdxType="Header" />
    <h4 {...{
      "id": "default-manual-verification-flow-entry-point"
    }}>{`Default manual verification flow entry point`}</h4>
    <p>{`When using the default flow, the `}<strong parentName="p">{`Link with account numbers`}</strong>{` entry point will appear when the user does any of the following:`}</p>
    <ul>
      <li parentName="ul">{`Encounters an error in the Link flow`}</li>
      <li parentName="ul">{`Selects an institution whose connection health is poor`}</li>
      <li parentName="ul">{`Attempts to close the Link modal without connecting an institution`}</li>
      <li parentName="ul">{`Receives a "no results" message when searching for an institution`}</li>
      <li parentName="ul">{`Scrolls to the bottom of the institution search results`}</li>
    </ul>
    <p>{`To trigger this flow via the `}<a parentName="p" {...{
        "href": "https://plaid.com/link-demo"
      }}>{`Link demo`}</a>{` specify `}<strong parentName="p">{`Auth`}</strong>{` in the Product dropdown menu and then `}<strong parentName="p">{`Launch Demo`}</strong>{`. Trigger empty search results (type in a search query like 'xyz') or the Exit Pane (by closing Link) and select `}<strong parentName="p">{`Link with account numbers`}</strong>{`.`}</p>
    <p>{`This entry point will direct the user to Database Auth, Instant Micro-deposits, or Same-Day Micro-deposits, depending on which fallback options you have enabled and on whether the institution supports Instant ACH.`}</p>
    <p>{`You can change these entry points using the features Auth Type Select and Reroute to Credentials. `}</p>
    <p>{`Auth Type Select adds a prompt for the end user to pick a manual Auth flow upfront, without hitting any of the triggers above.`}</p>
    <p>{`Reroute to Credentials is the opposite: it guides or forces the end user away from manual Auth flows if Plaid detects that the end user's institution is supported with a non-manual flow.`}</p>
    <h4 {...{
      "id": "adding-manual-verification-entry-points-with-auth-type-select"
    }}>{`Adding manual verification entry points with Auth Type Select`}</h4>
    <Image src='/assets/img/docs/auth/auth_type_select_manual.png' alt="Plaid auth manual link flow, select bank link option: Instantly or Manually. Continue button visible at the bottom." expandable={true} mdxType="Image" />
    <p>{`Auth Type Select is a Link configuration that shows a pane upfront, enabling end users to actively choose between
credential-based authentication and manual account authentication at the start of the Plaid Link flow.`}</p>
    <p>{`To demo `}<strong parentName="p">{`Auth Type Select`}</strong>{` via the `}<a parentName="p" {...{
        "href": "https://plaid.com/link-demo"
      }}>{`Plaid Link demo`}</a>{`, specify `}<strong parentName="p">{`United States`}</strong>{` and `}<strong parentName="p">{`Auth`}</strong>{` in the Country and Product dropdown menu respectively. Toggle on Auth Type Select, then `}<strong parentName="p">{`Launch Demo`}</strong>{`.`}</p>
    <p>{`To enable Auth Type Select, use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Dashboard Account Verification pane`}</a>{`, or, if not using the Dashboard Account Verification pane, set `}<inlineCode parentName="p">{`auth.auth_type_select_enabled: true`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    <Image src='/assets/img/docs/embedded-link-ats.gif' alt="Embedded institution search with Auth Type Select enabled, showing bank options and payment plan selection for PetGuard insurance." caption='Embedded Institution Search with Auth Type Select enabled' expandable={true} mdxType="Image" />
    <h5 {...{
      "id": "when-to-use-auth-type-select"
    }}>{`When to use Auth Type Select`}</h5>
    <p>{`Auth Type Select is best for customers who expect that a substantial portion of their consumers would prefer to authenticate manually, and who prefer to
maximize Auth coverage over other data products. Examples may include business account verification use cases where a high percentage of users are not expected to have
access to credentials of a bank account.`}</p>
    <p>{`Customers that see the greatest success with the Auth Type Select configuration have a substantial portion of users (over half, as a rule
of thumb) who cannot or will not connect by logging into their bank, who have invested in fraud risk mitigations or have a low risk use
case, and/or have very high intent users (such as users connecting a bank account to receive a payment).`}</p>
    <p>{`Some customers observe an increase in conversion of verified Auth data with this configuration. However, this configuration reduces coverage of other
products. When offered the manual option upfront, more users may choose to link manually. Users who opt to connect via micro-deposits can have lower conversion than Instant Auth because
the flow requires more steps for a user, including potentially returning to Link the next day to verify micro-deposits. It may encourage users to
connect via micro-deposits who would otherwise connect via credential-based Auth types (if the manual option was not available
upfront).`}</p>
    <p>{`This configuration is best for customers who want to maximize Auth coverage and can tolerate reduced coverage of other products like
Balance and Identity.`}</p>
    <h4 {...{
      "id": "removing-manual-verification-entry-points-with-reroute-to-credentials"
    }}>{`Removing manual verification entry points with Reroute to Credentials`}</h4>
    <p>{`Reroute to Credentials will prompt users to use a login-based verification flow if they attempt to use a manual verification flow with an institution where login-based flows are supported. `}<a parentName="p" {...{
        "href": "#optional-reroute"
      }}>{`Optional Reroute to Credentials`}</a>{` (the default Link flow) provides a nudge to use a login-based flow, but will not block manual verification, whereas `}<a parentName="p" {...{
        "href": "#forced-reroute"
      }}>{`Forced Reroute to Credentials`}</a>{` will block manual verification if login-based flows are available. Alternatively, you can `}<a parentName="p" {...{
        "href": "#no-reroute"
      }}>{`disable Reroute to Credentials`}</a>{` entirely.`}</p>
    <Image src='/assets/img/docs/auth/reroute-overview.png' caption='Forced Reroute to Credentials example flow' expandable={true} mdxType="Image" />
    <p>{`In some cases, when rerouting a user to a supported institution, Plaid may show a list of institutions instead of a single institution. This can occur when a financial institution has multiple different brands that share a common routing number.`}</p>
    <h5 {...{
      "id": "optional-reroute"
    }}>{`Optional Reroute`}</h5>
    <p>{`Optional Reroute to Credentials is the default flow. It is best for customers who would like to make a recommendation for the user to connect via credentials, but not block the user from proceeding with the Same-Day Micro-deposits flow. This flow will provide a reminder to the user that the institution is supported, and detail the benefits of instant connection, while also leaving the manual verification option in place. To enable Optional Reroute, you do not need to take any action. `}</p>
    <p>{`If you have disabled Optional Reroute (by enabling `}<a parentName="p" {...{
        "href": "#forced-reroute"
      }}>{`Forced Reroute`}</a>{` or `}<a parentName="p" {...{
        "href": "#no-reroute"
      }}>{`No Reroute`}</a>{`), you can re-enable it by using the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Dashboard Account Verification pane`}</a>{`, or set `}<inlineCode parentName="p">{`auth.reroute_to_credentials: "OPTIONAL"`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Single Institution Optional Reroute`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Multiple Institution Optional Reroute`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Image className={styles.maxWidthInitial} src='/assets/img/docs/auth/reroute_to_credentials_1.png' alt="Sign in to Gingham Bank via Plaid using routing number 12341234. Tap 'Continue' or 'Enter account number instead.'" width='256' expandable={true} mdxType="Image" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><Image className={styles.maxWidthInitial} src='/assets/img/docs/auth/reroute_to_credentials_2.png' alt="Mobile screen with options to select a financial institution. Choices: Gingham Bank, Herringbone Treasury. Option to enter account number." width='256' expandable={true} mdxType="Image" /></td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "forced-reroute"
    }}>{`Forced Reroute`}</h5>
    <p>{`Forced Reroute to Credentials is best for customers who would like to restrict the user to connect via credentials if the institution is supported on a credential-based flow
and stop the user from proceeding with a manual flow. This flow is designed to block a user from connecting manually for institutions that are supported
via credentials in high ACH risk use cases. To enable Forced Reroute, use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Dashboard Account Verification pane`}</a>{`, or set `}<inlineCode parentName="p">{`auth.reroute_to_credentials: "FORCED"`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Single Institution Forced Reroute`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Multiple Institution Forced Reroute`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Image className={styles.maxWidthInitial} src='/assets/img/docs/auth/reroute_to_credentials_3.png' alt="Sign in to Gingham Bank using Wonderwallet with routing number 12341234. Press 'Continue' to securely connect your account." width='256' expandable={true} mdxType="Image" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><Image className={styles.maxWidthInitial} src='/assets/img/docs/auth/reroute_to_credentials_4.png' alt="Select an institution based on routing number 12345678. Options: Gingham Bank, Herringbone Treasury. Sign in required." width='256' expandable={true} mdxType="Image" /></td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "no-reroute"
    }}>{`No Reroute`}</h5>
    <p>{`To completely disable Reroute to Credentials, use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Dashboard Account Verification pane`}</a>{`, or set `}<inlineCode parentName="p">{`auth.reroute_to_credentials: "OFF"`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "default-manual-verification-flow-entry-point",
        level: 4,
        title: "Default manual verification flow entry point",
      },
{
        id: "adding-manual-verification-entry-points-with-auth-type-select",
        level: 4,
        title: "Adding manual verification entry points with Auth Type Select",
      },
{
        id: "removing-manual-verification-entry-points-with-reroute-to-credentials",
        level: 4,
        title: "Removing manual verification entry points with Reroute to Credentials",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
